/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ExternalActionTypeProps {
  id: string;
  actionTypeName: string;
  actionTypeDescription: string;
  priority: number;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ExternalActionTypeRecord extends Record<ExternalActionTypeProps>({
  id: null as any,
  actionTypeName: null as any,
  actionTypeDescription: null as any,
  priority: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    actionTypeName: string;
    actionTypeDescription: string;
    priority: number;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ExternalActionTypeRecord {
    if (json == null) {
      throw new Error('ExternalActionTypeRecord.fromJS: parameter cannot be null');
    }
    return new ExternalActionTypeRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ExternalActionTypeJson.id',
      ),
      actionTypeName: getFromOrThrow(
        json,
        (j) => j.actionTypeName,
        'ExternalActionTypeJson.actionTypeName',
      ),
      actionTypeDescription: getFromOrThrow(
        json,
        (j) => j.actionTypeDescription,
        'ExternalActionTypeJson.actionTypeDescription',
      ),
      priority: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'priority', 0),
        'ExternalActionTypeJson.priority',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    actionTypeName: string;
    actionTypeDescription: string;
    priority: number;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ExternalActionTypeRecord, Error[]] {
    if (json == null) {
      throw new Error('ExternalActionTypeRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ExternalActionTypeRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ExternalActionTypeJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      actionTypeName: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => j.actionTypeName, 'ExternalActionTypeJson.actionTypeName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      actionTypeDescription: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.actionTypeDescription,
            'ExternalActionTypeJson.actionTypeDescription',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      priority: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'priority', 0),
            'ExternalActionTypeJson.priority',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ExternalActionTypeProps) {
    super(props);
    if (props == null) {
      throw new Error('ExternalActionTypeRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ExternalActionTypeViewModelBase implements ExternalActionTypeProps {
  protected readonly _props: ExternalActionTypeProps;
  private _json?: ExternalActionTypeProps;

  get id(): string {
    return this._props.id;
  }
  get actionTypeName(): string {
    return this._props.actionTypeName;
  }
  get actionTypeDescription(): string {
    return this._props.actionTypeDescription;
  }
  get priority(): number {
    return this._props.priority;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ExternalActionTypeProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        actionTypeName: this._props.actionTypeName,
        actionTypeDescription: this._props.actionTypeDescription,
        priority: this._props.priority,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllExternalActionTypeEntities = (state: AppState) =>
  state.entityStores.externalActionType.entities;

export const selectExternalActionTypeById = (id: string) => (state: AppState) =>
  state.entityStores.externalActionType.entities.get(id);

export const selectExternalActionTypeMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.externalActionType.meta.get(id);

export const selectExternalActionTypeAndMetadataById = (id: string) =>
  createSelector(
    selectExternalActionTypeById(id),
    selectExternalActionTypeMetadataById(id),
    (externalActionType, meta) => ({ externalActionType, meta }),
  );

export function selectExternalActionTypeEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllExternalActionTypeEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ExternalActionTypeRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectExternalActionTypeEntitiesByActionTypeName(actionTypeName: string) {
  return createSelector(selectAllExternalActionTypeEntities, (entities) =>
    entities.filter((e) => e.actionTypeName === actionTypeName).valueSeq(),
  );
}

export function selectExternalActionTypeEntitiesByActionTypeDescription(
  actionTypeDescription: string,
) {
  return createSelector(selectAllExternalActionTypeEntities, (entities) =>
    entities.filter((e) => e.actionTypeDescription === actionTypeDescription).valueSeq(),
  );
}

export function selectExternalActionTypeEntitiesByPriority(priority: number) {
  return createSelector(selectAllExternalActionTypeEntities, (entities) =>
    entities.filter((e) => e.priority === priority).valueSeq(),
  );
}
//#endregion Selectors
